import React, { useEffect, useId, useState } from 'react';
import { Dropdown } from './Dropdown.jsx';

/**
 * The args form: one row per arg, name and description beside (or, when
 * narrow, above) a boxed control, in edl.dk's type and square checks. A tool
 * first: every input reads as one at a glance. Shared by the shell's panel and
 * the docs <Controls> block.
 *
 * argTypes: serialised (see preview/prepare.js serialiseArgTypes)
 * args: current values; values the browser cannot clone arrive as
 *       { __edlUnserialisable: 'function' } and render read-only.
 */
export function Controls({ argTypes = {}, args = {}, initialArgs = {}, onChange, onReset, compact = false, emptyText }) {
  const rows = Object.entries(argTypes).filter(([, t]) => t.control !== false && !t.disabled && t.control !== null);
  const hidden = Object.entries(argTypes).filter(([, t]) => t.control === null && !t.disabled && args[t.name] !== undefined);
  const changed = Object.keys(args).some((k) => JSON.stringify(args[k]) !== JSON.stringify(initialArgs[k]));

  if (!rows.length && !hidden.length) {
    return <p className="edl-controls__empty">{emptyText ?? 'This story has no args to change.'}</p>;
  }

  const groups = groupBy(rows, ([, t]) => t.category ?? '');
  const shuffleable = rows.filter(([key, t]) => !(args[key] && typeof args[key] === 'object' && args[key].__edlUnserialisable) && randomValue(t, args[key]) !== undefined);
  return (
    <div className={`edl-controls${compact ? ' edl-controls--compact' : ''}`}>
      {onReset && (
        <div className="edl-controls__bar">
          <span className="edl-controls__count">{rows.length} {rows.length === 1 ? 'arg' : 'args'}</span>
          <span className="edl-controls__actions">
            {shuffleable.length > 0 && (
              <button type="button" className="edl-link-button" onClick={() => onChange?.(shuffle(shuffleable, args))} title="Pick random values for every option, toggle and range">
                Shuffle
              </button>
            )}
            <button type="button" className="edl-link-button" onClick={onReset} disabled={!changed}>
              Reset
            </button>
          </span>
        </div>
      )}
      {[...groups].map(([category, items]) => (
        <div className="edl-controls__group" key={category || '_'}>
          {category && <p className="edl-controls__category">{category}</p>}
          {items.map(([key, type]) => (
            <ControlRow key={key} name={key} type={type} value={args[key]} initial={initialArgs[key]} onChange={(v) => onChange?.({ [key]: v })} />
          ))}
        </div>
      ))}
      {hidden.length > 0 && (
        <div className="edl-controls__group">
          <p className="edl-controls__category">Fixed in this story</p>
          {hidden.map(([key, type]) => (
            <div className="edl-field edl-field--static" key={key}>
              <span className="edl-field__label">{type.name ?? key}</span>
              <div className="edl-field__control">
                <code className="edl-field__static">{preview(args[key])}</code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A random value for args with a finite choice: booleans, options, bounded
// numbers. Text, objects, colours and dates are left alone (undefined).
function randomValue(type, current) {
  const control = type.control ?? {};
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  switch (control.type) {
    case 'boolean':
      return Math.random() < 0.5;
    case 'select':
    case 'radio':
    case 'inline-radio':
      return type.options?.length > 1 ? pick(type.options) : undefined;
    case 'multi-select':
    case 'check':
    case 'inline-check':
      return type.options?.length ? type.options.filter(() => Math.random() < 0.5) : undefined;
    case 'range':
    case 'number': {
      const { min, max, step = 1 } = control;
      if (min == null || max == null) return undefined;
      return Math.min(max, min + Math.round((Math.random() * (max - min)) / step) * step);
    }
    default:
      return undefined;
  }
}

function shuffle(rows, args) {
  const patch = {};
  for (const [key, type] of rows) patch[key] = randomValue(type, args[key]);
  return patch;
}

function ControlRow({ name, type, value, initial, onChange }) {
  const id = useId();
  const control = type.control ?? {};
  const label = type.name ?? name;
  const unserialisable = value && typeof value === 'object' && value.__edlUnserialisable;
  const modified = JSON.stringify(value) !== JSON.stringify(initial);

  let field;
  if (unserialisable) {
    field = <code className="edl-field__static">{value.__edlUnserialisable}</code>;
  } else {
    switch (control.type) {
      case 'boolean':
        field = <BooleanField id={id} value={Boolean(value)} onChange={onChange} />;
        break;
      case 'number':
      case 'range':
        field = <NumberField id={id} value={value} control={control} onChange={onChange} range={control.type === 'range'} />;
        break;
      case 'color':
        field = <ColorField id={id} value={value} onChange={onChange} />;
        break;
      case 'select':
        field = <SelectField id={id} value={value} options={type.options ?? []} labels={type.labels} onChange={onChange} />;
        break;
      case 'multi-select':
      case 'check':
      case 'inline-check':
        field = <CheckField id={id} value={Array.isArray(value) ? value : []} options={type.options ?? []} labels={type.labels} onChange={onChange} inline={control.type !== 'check'} />;
        break;
      case 'radio':
      case 'inline-radio':
        field = <RadioField id={id} value={value} options={type.options ?? []} labels={type.labels} onChange={onChange} inline={control.type === 'inline-radio' || (type.options ?? []).length <= 4} />;
        break;
      case 'object':
        field = <ObjectField id={id} value={value} onChange={onChange} />;
        break;
      case 'date':
        field = <DateField id={id} value={value} onChange={onChange} />;
        break;
      case 'file':
        field = <span className="edl-field__static">File inputs are not supported yet.</span>;
        break;
      case 'text':
      default:
        field = <TextField id={id} value={value} onChange={onChange} multiline={typeof value === 'string' && value.length > 60} />;
    }
  }

  return (
    <div className={`edl-field edl-field--${control.type ?? 'text'}${modified ? ' is-modified' : ''}`}>
      <label className="edl-field__label" htmlFor={id}>
        {label}
        {type.required && <span className="edl-field__required" aria-label="required">*</span>}
      </label>
      <div className="edl-field__control">{field}</div>
      {type.description && <p className="edl-field__description">{inlineMarkdown(type.description)}</p>}
    </div>
  );
}

function TextField({ id, value, onChange, multiline }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const commit = (v) => {
    setDraft(v);
    onChange(v);
  };
  if (multiline) {
    return <textarea id={id} className="edl-input edl-input--area" rows={3} value={draft} onChange={(e) => commit(e.target.value)} />;
  }
  return <input id={id} className="edl-input" type="text" value={draft} onChange={(e) => commit(e.target.value)} />;
}

function NumberField({ id, value, control, onChange, range }) {
  const parse = (v) => (v === '' ? undefined : Number(v));
  return (
    <div className="edl-number">
      {range && (
        <input
          className="edl-range"
          type="range"
          min={control.min ?? 0}
          max={control.max ?? 100}
          step={control.step ?? 1}
          value={value ?? control.min ?? 0}
          onChange={(e) => onChange(parse(e.target.value))}
          aria-hidden="true"
          tabIndex={-1}
        />
      )}
      <input id={id} className="edl-input edl-input--number" type="number" min={control.min} max={control.max} step={control.step} value={value ?? ''} onChange={(e) => onChange(parse(e.target.value))} />
    </div>
  );
}

function BooleanField({ id, value, onChange }) {
  return (
    <label className="edl-check" htmlFor={id}>
      <input id={id} type="checkbox" className="edl-check__input" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="edl-check__box" aria-hidden="true" />
      <span className="edl-check__text">{value ? 'True' : 'False'}</span>
    </label>
  );
}

function optionLabel(option, labels) {
  const key = typeof option === 'string' || typeof option === 'number' ? option : JSON.stringify(option);
  return labels?.[key] ?? String(key);
}

function SelectField({ id, value, options, labels, onChange }) {
  const index = options.findIndex((o) => JSON.stringify(o) === JSON.stringify(value));
  return <Dropdown id={id} value={index} options={options.map((o, i) => ({ value: i, label: optionLabel(o, labels) }))} onChange={(i) => onChange(options[i])} />;
}

function RadioField({ id, value, options, labels, onChange, inline }) {
  return (
    <div className={`edl-choices${inline ? ' edl-choices--inline' : ''}`} role="radiogroup" aria-labelledby={id}>
      {options.map((o, i) => {
        const checked = JSON.stringify(o) === JSON.stringify(value);
        return (
          <label className="edl-check" key={i}>
            <input type="radio" className="edl-check__input" name={id} checked={checked} onChange={() => onChange(o)} id={i === 0 ? id : undefined} />
            <span className="edl-check__box" aria-hidden="true" />
            <span className="edl-check__text">{optionLabel(o, labels)}</span>
          </label>
        );
      })}
    </div>
  );
}

function CheckField({ id, value, options, labels, onChange, inline }) {
  const has = (o) => value.some((v) => JSON.stringify(v) === JSON.stringify(o));
  return (
    <div className={`edl-choices${inline ? ' edl-choices--inline' : ''}`}>
      {options.map((o, i) => (
        <label className="edl-check" key={i}>
          <input
            type="checkbox"
            className="edl-check__input"
            id={i === 0 ? id : undefined}
            checked={has(o)}
            onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((v) => JSON.stringify(v) !== JSON.stringify(o)))}
          />
          <span className="edl-check__box" aria-hidden="true" />
          <span className="edl-check__text">{optionLabel(o, labels)}</span>
        </label>
      ))}
    </div>
  );
}

function ColorField({ id, value, onChange }) {
  const hex = typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';
  return (
    <div className="edl-color">
      <input className="edl-color__swatch" type="color" value={hex} onChange={(e) => onChange(e.target.value)} aria-label="Pick a colour" />
      <input id={id} className="edl-input" type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function DateField({ id, value, onChange }) {
  const d = value ? new Date(value) : null;
  const str = d && !Number.isNaN(d.valueOf()) ? d.toISOString().slice(0, 10) : '';
  return <input id={id} className="edl-input" type="date" value={str} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).valueOf() : undefined)} />;
}

function ObjectField({ id, value, onChange }) {
  const [draft, setDraft] = useState(() => JSON.stringify(value ?? null, null, 2));
  const [error, setError] = useState(null);
  useEffect(() => {
    setDraft(JSON.stringify(value ?? null, null, 2));
    setError(null);
  }, [JSON.stringify(value)]);
  return (
    <>
      <textarea
        id={id}
        className={`edl-input edl-input--area edl-input--code${error ? ' is-invalid' : ''}`}
        rows={Math.min(10, draft.split('\n').length + 1)}
        spellCheck={false}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            setError(null);
            onChange(parsed);
          } catch (err) {
            setError('Not valid JSON yet');
          }
        }}
      />
      {error && <p className="edl-field__error">{error}</p>}
    </>
  );
}

function preview(v) {
  if (v && typeof v === 'object' && v.__edlUnserialisable) return v.__edlUnserialisable;
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 77) + '…' : s;
  } catch {
    return String(v);
  }
}

function groupBy(items, fn) {
  const map = new Map();
  for (const item of items) {
    const k = fn(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(item);
  }
  return map;
}

/** `code`, **bold**, *em* and [links](url) in one-line descriptions. */
export function inlineMarkdown(text) {
  const parts = [];
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) parts.push(<code key={i++}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('**')) parts.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('*')) parts.push(<em key={i++}>{tok.slice(1, -1)}</em>);
    else {
      const [, label, href] = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      parts.push(<a key={i++} href={href} target="_blank" rel="noreferrer">{label}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
